/**
 * Auto-recover from stale-chunk dynamic-import failures after a deploy. (B-149)
 *
 * SPA deploys rotate hashed chunk filenames. A browser still holding the
 * PREVIOUS build requests now-nonexistent chunks when it lazy-loads a route:
 * the `import()` rejects, `React.lazy` throws, and `RouteErrorBoundary`
 * strands the user behind an error message until a manual hard refresh.
 *
 * Vite fires a `vite:preloadError` window event on such a preload failure
 * (Vite >=3). We reload once to pull the new index.html + chunk graph. A
 * short-TTL sessionStorage guard prevents a reload LOOP if a chunk is
 * genuinely missing/broken — after one attempt within the window we stop and
 * let the existing error boundary surface the failure.
 */
const RELOAD_GUARD_KEY = 'trf_chunk_reload_at';
const RELOAD_GUARD_MS = 10_000;

export function installChunkReloadHandler(): void {
  if (typeof window === 'undefined') return;

  window.addEventListener('vite:preloadError', (event: Event) => {
    let last = 0;
    try {
      last = Number(sessionStorage.getItem(RELOAD_GUARD_KEY) ?? 0);
    } catch {
      /* sessionStorage unavailable (private mode / blocked) — treat as never */
    }

    // Already reloaded recently: a fresh build would have loaded by now, so the
    // chunk is genuinely broken. Don't loop — let the error boundary show it.
    if (Date.now() - last < RELOAD_GUARD_MS) return;

    try {
      sessionStorage.setItem(RELOAD_GUARD_KEY, String(Date.now()));
    } catch {
      /* ignore — best-effort guard */
    }

    // Stop Vite from rethrowing the preload error, then reload.
    event.preventDefault();
    window.location.reload();
  });
}
